"use client";
import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  FormHelperText,
  Alert,
  Box,
  IconButton,
} from "@mui/material";
import { Paperclip, FileText, X } from "lucide-react";
import { MAX_UPLOAD_BYTES, UPLOAD_TOO_LARGE_MESSAGE } from "../../api/documentsApi";
import { createProject, type Project } from "../../api/projects";
import { useAuthedApi } from "../../api/useAuthedApi";
import { Row } from "../Common/Row";
import { PortalScope } from "@/analytics/PortalScope";

const MAX_INSTRUCTIONS_BYTES = 8 * 1024;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the freshly created project so the caller can navigate to it
   *  or refresh its list. */
  onCreated: (project: Project) => void;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Claude-style "new project" dialog: a name, optional instructions the
 * assistant follows for every chat in the project, and an optional set of
 * files. Files need a project_id, which only exists after creation, so the
 * flow is: create the project, then upload the staged files to it (as
 * searchable project knowledge) before handing back to the caller.
 */
export function CreateProjectDialog({ open, onClose, onCreated }: Props) {
  const { documents } = useAuthedApi();
  const [name, setName] = React.useState("");
  const [instructions, setInstructions] = React.useState("");
  const [files, setFiles] = React.useState<File[]>([]);
  /** Names of the last pick's files that were over the upload limit. */
  const [refused, setRefused] = React.useState<string[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [phase, setPhase] = React.useState<"idle" | "creating" | "uploading">("idle");
  const [error, setError] = React.useState<string | null>(null);
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  const instructionsTooLong =
    new TextEncoder().encode(instructions).length > MAX_INSTRUCTIONS_BYTES;
  const canSubmit = name.trim().length > 0 && !instructionsTooLong && !saving;

  // Reset fields whenever the dialog is (re)opened.
  React.useEffect(() => {
    if (open) {
      setName("");
      setInstructions("");
      setFiles([]);
      setRefused([]);
      setError(null);
      setSaving(false);
      setPhase("idle");
    }
  }, [open]);

  const addFiles = (picked: FileList | null) => {
    if (!picked) return;
    const all = Array.from(picked);
    // Refuse oversized files now, while the user is looking: staged files
    // upload only after the project exists, and a failure there reaches the
    // console, not the user.
    setRefused(all.filter((f) => f.size > MAX_UPLOAD_BYTES).map((f) => f.name));
    const next = all.filter((f) => f.size <= MAX_UPLOAD_BYTES);
    // De-dupe by name+size so picking the same file twice doesn't stack.
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      return [...prev, ...next.filter((f) => !seen.has(`${f.name}:${f.size}`))];
    });
  };

  const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    let project: Project;
    try {
      setPhase("creating");
      project = await createProject({
        name: name.trim(),
        instructions: instructions.trim() || undefined,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
      setPhase("idle");
      return;
    }

    // Upload staged files to the new project as searchable knowledge. The
    // project already exists at this point, so a partial/failed upload still
    // leaves a usable project — we surface a soft warning and hand off, and
    // the project page shows per-file ingest status.
    if (files.length > 0 && documents) {
      setPhase("uploading");
      const failed: string[] = [];
      for (const file of files) {
        try {
          await documents.upload({ file, intent: "knowledge", project_id: project.id });
        } catch {
          failed.push(file.name);
        }
      }
      if (failed.length > 0) {
        // Don't block navigation — the project is created. Log for the dev
        // console; the project page will reflect what actually landed.
        // eslint-disable-next-line no-console
        console.warn("Some project files failed to upload:", failed);
      }
    }

    onCreated(project);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      {/* Portaled, so it declares its full path; the area says where it was opened
          from. Named for what it makes, as the composer opens it too. */}
      <PortalScope section="project_dialog">
      <DialogTitle sx={{ fontWeight: 700 }}>New project</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Group related chats, give Artie instructions to follow, and add
            files it can draw on — all scoped to this project.
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Project name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Q3 Launch"
            fullWidth
            autoFocus
            disabled={saving}
            slotProps={{ htmlInput: { maxLength: 200, "aria-label": "project name" } }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSubmit) {
                e.preventDefault();
                submit();
              }
            }}
          />

          <div>
            <TextField
              label="Instructions (optional)"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Describe how Artie should behave in this project — tone, role, what to focus on, formats to use…"
              fullWidth
              multiline
              minRows={4}
              disabled={saving}
              error={instructionsTooLong}
              slotProps={{ htmlInput: { "aria-label": "instructions" } }}
            />
            <FormHelperText error={instructionsTooLong}>
              {instructionsTooLong
                ? "Instructions are too long (max 8 KB)."
                : "Applied to every chat started in this project."}
            </FormHelperText>
          </div>

          {/* Staged files — uploaded as searchable project knowledge after the
              project is created. */}
          <div>
            <Row align="center" justify="space-between" gap={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Files (optional)
              </Typography>
              <Button
                size="small"
                data-ph="upload"
                startIcon={<Paperclip size={14} />}
                onClick={() => fileRef.current?.click()}
                disabled={saving || !documents}
              >
                Add files
              </Button>
              <input
                ref={fileRef}
                type="file"
                multiple
                hidden
                aria-label="add project files"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </Row>
            <FormHelperText>
              Added files become searchable knowledge Artie can use in this project’s chats.
            </FormHelperText>
            {refused.map((fileName) => (
              <FormHelperText key={fileName} error>
                {`${fileName} wasn’t added. ${UPLOAD_TOO_LARGE_MESSAGE}`}
              </FormHelperText>
            ))}
            {files.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {files.map((f, i) => (
                  <Row key={`${f.name}:${f.size}:${i}`} align="center" gap={1} sx={{ minWidth: 0 }}>
                    <FileText size={16} aria-hidden style={{ flexShrink: 0 }} />
                    <Typography
                      variant="body2"
                      sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {f.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {formatBytes(f.size)}
                    </Typography>
                    <IconButton
                      size="small"
                      data-ph="delete"
                      onClick={() => removeFile(i)}
                      disabled={saving}
                      aria-label={`remove ${f.name}`}
                    >
                      <X size={14} />
                    </IconButton>
                  </Row>
                ))}
              </Stack>
            )}
            {!documents && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Sign-in required to attach files.
                </Typography>
              </Box>
            )}
          </div>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button data-ph="cancel" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" data-ph="create" onClick={submit} disabled={!canSubmit}>
          {phase === "creating"
            ? "Creating…"
            : phase === "uploading"
            ? "Uploading files…"
            : "Create project"}
        </Button>
      </DialogActions>
      </PortalScope>
    </Dialog>
  );
}
